import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  FormHelperText
} from '@mui/material';
import { Order } from 'src/models/order';
import X from 'src/icons/X';
import { useApiMutation } from 'src/hooks/useApiMutation';
import { LoadingButton, DatePicker, LocalizationProvider } from '@mui/lab';
import DateFnsUtils from '@mui/lab/AdapterDateFns';
import useSettings from 'src/hooks/useSettings';

interface WarrantyEventModalProps {
  open: boolean;
  onClose: () => void;
  order: Order;
}
export const creditStatusMap: { [key: string]: string } = {
  NO_CREDIT: 'No Credit',
  MAYBE_CREDIT: 'Maybe Credit',
  EXPECTING_CREDIT: 'Expecting a Credit'
};
export const reasonMap: {
  [key: string]: {
    name: string;
    subReasons: { value: string; name: string; creditStatus?: string }[];
  };
} = {
  powerIssues: {
    name: 'Power Issues',
    subReasons: [
      { value: 'WONT_TURN_ON', name: "Won't Turn On" },
      { value: 'WONT_CHARGE', name: "Won't charge" },
      { value: 'BATTERY', name: 'Battery' },
      { value: 'WONT_TURN_OFF', name: "Won't Turn off" }
    ]
  },
  hardwarePhysical: {
    name: 'Hardware / Physical',
    subReasons: [
      { value: 'BROKEN', name: 'Broken' },
      { value: 'RIGHT_AUDIO', name: 'Right Audio (previously Earphone Audio)' },
      { value: 'OTHER_AUDIO', name: 'Other Audio' },
      { value: 'RESET_NOT_WORKING', name: 'Reset Not Working' },
      { value: 'PURPLE_LIGHT_STUCK', name: 'Purple light stuck on' }
    ]
  },
  chipError: {
    name: 'Chip Error',
    subReasons: [
      { value: 'ERROR_LIGHT_SEQUENCE', name: 'Error light sequence' },
      { value: 'EEG_ADC', name: 'EEG/ADC' },
      { value: 'PULSE_OX', name: 'Pulse Ox' },
      { value: 'AUDIO_CHIP', name: 'Audio Chip' },
      { value: 'MEMORY', name: 'Memory' },
      { value: 'NIR_LED_DRIVER', name: 'NIR LED Driver' }
    ]
  },
  firmware: {
    name: 'Firmware',
    subReasons: [{ value: 'FIRMWARE_ISSUE', name: 'Firmware Issue' }]
  },
  connectivityIssues: {
    name: 'Connectivity Issues',
    subReasons: [
      { value: 'BLUETOOTH', name: 'Bluetooth' },
      { value: 'SIGNAL_CHECK', name: 'Signal check' },
      { value: 'PULSEOX', name: 'PulseOx' },
      { value: 'ELECTRODE', name: 'Electrode' }
    ]
  },
  accessories: {
    name: 'Accessories',
    subReasons: [
      { value: 'GENIUS_PULSE', name: 'Genius Pulse' },
      { value: 'PSU', name: 'PSU' },
      { value: 'BROKEN_ELECTRODE', name: 'Broken electrode' }
    ]
  },
  other: {
    name: 'Other',
    subReasons: [{ value: 'OTHER', name: 'Other' }]
  }
};

const WarrantyEventModal: FC<WarrantyEventModalProps> = ({
  open,
  onClose,
  order
}) => {
  const [processWarrantyEvent, { loading }] = useApiMutation(
    'billing',
    'ProcessWarrantyEvent'
  );

  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [subReasonList, setSubReasonList] = useState<
    { value: string; name: string }[]
  >([]);
  const { settings } = useSettings();

  const changeTimezone = (date: Date, tzCode?: string) => {
    if (!tzCode) {
      return date;
    }
    const invdate = new Date(
      date.toLocaleString('en-US', {
        timeZone: tzCode
      })
    );
    var diff = date.getTime() - invdate.getTime();
    return new Date(date.getTime() - diff);
  };

  const [formData, setFormData] = useState({
    warranteedUnitShippedDate: order.shippedDate?.formatted
      ? new Date(order.shippedDate.formatted)
      : new Date(),
    warrantyCreated: new Date(),
    serialNo: 'S0001-',
    reason: '',
    subReason: '',
    creditStatus: 'NO_CREDIT',
    notes: '',
    batchNo: '',
    unitRetrieved: false,
    haveSensorsBeenReplaced: false,
    headsetMAC: ''
  });

  const handleInputChange = (field: string, value: string | boolean | Date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    switch (field) {
      case 'reason':
        if (typeof value === 'string') {
          const subReasons = reasonMap[value]?.subReasons || [];
          setSubReasonList(subReasons);
          handleInputChange(
            'subReason',
            subReasons.length === 1 ? subReasons[0].value : ''
          );
        }
        break;
      case 'subReason':
        if (typeof value === 'string') {
          const selectedReason = reasonMap[formData.reason];
          const selectedSubReason = selectedReason?.subReasons.find(
            (sub) => sub.value === value
          );
          if (selectedSubReason && selectedSubReason.creditStatus) {
            handleInputChange('creditStatus', selectedSubReason.creditStatus);
          }
        }
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    try {
      if (formData.serialNo.length <= 6) {
        throw new Error('Serial Number is required S0001-XXX.');
      }
      if (formData.batchNo.length < 1) {
        throw new Error('Batch Number is required.');
      }
      if (formData.reason.length < 1) {
        throw new Error('Reason is required.');
      }
      if (formData.subReason.length < 1) {
        throw new Error('Sub Reason is required.');
      }

      setErrorMessage('');
      setSubmitLoading(true);
      const variables = {
        orderId: order.id,
        serialNo: formData.serialNo,
        warranteedUnitShippedDate: changeTimezone(
          new Date(
            formData.warranteedUnitShippedDate.toISOString().split('T')[0] +
              'T00:00:00'
          ),
          settings.timeZone
        ).toISOString(),
        warrantyCreated: changeTimezone(
          new Date(
            formData.warrantyCreated.toISOString().split('T')[0] + 'T00:00:00'
          ),
          settings.timeZone
        ).toISOString(),
        reason: formData.reason || null,
        subReason: formData.subReason || null,
        creditStatus: formData.creditStatus || null,
        notes: formData.notes || null,
        batchNo: formData.batchNo || null,
        unitRetrieved: formData.unitRetrieved,
        haveSensorsBeenReplaced: formData.haveSensorsBeenReplaced,
        headsetMAC: formData.headsetMAC || null
      };
      console.log('Warranty Event Processed input:', variables);
      const response = await processWarrantyEvent({ variables });
      console.log('Warranty Event Processed:', response);

      const warrantyEvent = response?.data?.ProcessWarrantyEvent;
      order.warrantyEvents.push(warrantyEvent);
      setFormData({
        warranteedUnitShippedDate: order.shippedDate?.formatted
          ? new Date(order.shippedDate.formatted)
          : new Date(),
        warrantyCreated: new Date(),
        serialNo: 'S0001-',
        reason: '',
        subReason: '',
        creditStatus: 'NO_CREDIT',
        notes: '',
        batchNo: '',
        unitRetrieved: false,
        haveSensorsBeenReplaced: false,
        headsetMAC: ''
      });
      setSubmitLoading(false);
      onClose();
    } catch (error) {
      console.error('Error processing warranty event:', error);
      setErrorMessage(error.message);
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form and close
    setFormData({
      warranteedUnitShippedDate: order.shippedDate?.formatted
        ? new Date(order.shippedDate.formatted)
        : new Date(),
      warrantyCreated: new Date(),
      serialNo: 'S0001-',
      reason: '',
      subReason: '',
      creditStatus: 'NO_CREDIT',
      notes: '',
      batchNo: '',
      unitRetrieved: false,
      haveSensorsBeenReplaced: false,
      headsetMAC: ''
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <Box>
            Process Warranty
            <Typography variant="body2" color="textSecondary">
              Order: {order.confirmationCode}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ mt: -1, mr: -1 }}
          >
            <X fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={DateFnsUtils}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Warranty Created"
                  value={formData.warrantyCreated}
                  onChange={(newValue) => {
                    if (newValue && !isNaN(newValue.getTime())) {
                      handleInputChange('warrantyCreated', newValue);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Warranteed Unit Shipped Date"
                  value={formData.warranteedUnitShippedDate}
                  onChange={(newValue) => {
                    if (newValue && !isNaN(newValue.getTime())) {
                      handleInputChange('warranteedUnitShippedDate', newValue);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                {order.warrantyEvents && order.warrantyEvents.length > 0 && (
                  <FormHelperText sx={{ color: 'warning.main', mt: 1 }}>
                    ⚠️ A warranty event already exists, double check this date.
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Serial Number"
                  required
                  value={formData.serialNo}
                  onChange={(e) =>
                    handleInputChange('serialNo', e.target.value)
                  }
                  placeholder="Enter serial number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Batch Number"
                  required
                  type="number"
                  value={formData.batchNo}
                  onChange={(e) => handleInputChange('batchNo', e.target.value)}
                  placeholder="Enter batch number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Reason</InputLabel>
                  <Select
                    value={formData.reason}
                    label="Reason"
                    required
                    onChange={(e) =>
                      handleInputChange('reason', e.target.value)
                    }
                  >
                    {Object.keys(reasonMap).map((reasonKey) => (
                      <MenuItem value={reasonKey}>
                        {reasonMap[reasonKey].name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Sub Reason</InputLabel>
                  <Select
                    value={formData.subReason}
                    label="Sub Reason"
                    required
                    disabled={subReasonList.length === 0}
                    onChange={(e) =>
                      handleInputChange('subReason', e.target.value)
                    }
                  >
                    {subReasonList.map((subReason) => (
                      <MenuItem value={subReason.value}>
                        {subReason.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Credit Status</InputLabel>
                  <Select
                    value={formData.creditStatus}
                    label="Credit Status"
                    onChange={(e) =>
                      handleInputChange('creditStatus', e.target.value)
                    }
                  >
                    {Object.keys(creditStatusMap).map((key) => (
                      <MenuItem value={key}>{creditStatusMap[key]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Headset MAC"
                  value={formData.headsetMAC}
                  onChange={(e) =>
                    handleInputChange('headsetMAC', e.target.value)
                  }
                  placeholder="Enter Headset MAC"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.unitRetrieved}
                      onChange={(e) =>
                        handleInputChange('unitRetrieved', e.target.checked)
                      }
                    />
                  }
                  label="Unit Retrieved"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.haveSensorsBeenReplaced}
                      onChange={(e) =>
                        handleInputChange(
                          'haveSensorsBeenReplaced',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Have Sensors Been Replaced"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes and details..."
                />
              </Grid>
            </Grid>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary" variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={submitLoading}
          loadingPosition="center"
        >
          {submitLoading ? '\u00A0' : 'Process'}
        </LoadingButton>
      </DialogActions>

      {errorMessage.length > 0 ? (
        <Grid item xs={12}>
          <Box sx={{ mx: 4, mb: 2 }}>
            <FormHelperText error>{errorMessage}</FormHelperText>
          </Box>
        </Grid>
      ) : null}
    </Dialog>
  );
};

export default WarrantyEventModal;
